import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./clinicSummary.module.css";
import calendarIcon from "../../images/calendar_icon.svg";
import React from "react";
import { calculateAge } from "../../utils/constants";
import BackArrowButton from "../../components/backArrowButton/backArrowButton";
import { IUserReport } from "../../services/types/users.types";
import { TClinicSummaryResponce } from "../../services/types/utils.types";
import RiskStatusBar from "../riskStatusBar/riskStatusBar";
import ClinicalBackground from "../clinicalBackground/clinicalBackground";
import ClinicRecommendations from "../clinicRecommendations/clinicRecommendations";
import personalIcon from '../../images/personal_icon.svg';
import clinicIcon from "../../images/clinic_icon.svg";
import { TClinicalSummaryLatest, TClinicalSummaryHistoryResponse } from "../../services/types/ai.types";
import MedicalReportsIcon from "../../images/medical_reports_icon.svg";
import intakeDayIcon from "../../images/intake_day_icon.svg";
import therapistReportsIcon from "../../images/therapist_reports_icon.svg";
import activeProgramIcon from "../../images/active_programs_icon.svg";
import inActiveProgramsIcon from "../../images/Inactive Programs_icon.svg";
import treatmentMeetingsIcon from "../../images/treatment_meetings_icon.svg";
import randomMeetingsIcon from "../../images/random_meetings_icon.svg";
import { useGetClinicalUserSummaryQuery } from "../../services/api/utils.api";
import { getPrintDate, parseSummaryText } from "../../utils/utils";
import { useGetClinicalSummaryHistoryQuery } from "../../services/api/ai.api";
import Loader from "../loader/loader";
import userCircleIcon from "../../images/user_circle_icon.svg";
import locationCircleIcon from "../../images/location_circle_icon.svg";
import lockCircleIcon from "../../images/lock_circle_icon.svg";

interface IClinicSummaryStatCardProps {
    icon: string;
    title: string;
    value: string | number | null | undefined;
    colorClass: string;
    isLoading?: boolean;
    textColRight?: boolean;
}

const ClinicSummaryStatCard = ({
    icon,
    title,
    value,
    colorClass,
    isLoading = false,
    textColRight = false
}: IClinicSummaryStatCardProps) => (
    <article className={colorClass}>
        <div className={styles.clinicSummary__iconRow} aria-hidden="true">
            <img src={icon} alt={title} className={styles.clinicSummary__icon} />
        </div>
        <div className={textColRight ? styles.textColRight : undefined}>
            <h3 className={`${styles.clinicSummary__text} ${styles.clinicSummary__text_bold}`}>
                {title}
            </h3>
            <p className={styles.clinicSummary__text}>
                {isLoading ? t('LOADING_LABEL') : value ?? t('UNKNOWN')}
            </p>
        </div>
    </article>
);

interface IProfileCardProps {
    icon: string;
    iconColorClass?: string;
    children: React.ReactNode;
}

const ProfileCard = ({ icon, iconColorClass, children }: IProfileCardProps) => (
    <article className={styles.clinicSummary__cardWhite}>
        <div className={`${styles.clinicSummary__iconCircle} ${iconColorClass || ""}`} aria-hidden="true">
            <img src={icon} alt="" className={styles.clinicSummary__icon} />
        </div>
        {children}
    </article>
);

const NoSummaryMessage = () => (
    <section className={styles.clinicSummary__bottomFullWidth} aria-label={t('CLINICAL_INFORMATION')}>
        <div className={styles.clinicSummary__noSummaryMessage}>
            {t('CLINICAL_SUMMARY_NOT_FOUND')}
        </div>
    </section>
);

interface IClinicalContentProps {
    latestSummary: TClinicalSummaryLatest | undefined;
    historyData: TClinicalSummaryHistoryResponse | undefined;
    userGuid: string | undefined;
}

const ClinicalContent = ({ latestSummary, historyData, userGuid }: IClinicalContentProps) => {
    const summaryText = (parseSummaryText(latestSummary?.summary) || latestSummary?.summary || "").trim();
    const clinicalInsightsText = (latestSummary?.clinicalInsights || "").trim();

    const hasSummary = latestSummary && (
        summaryText ||
        latestSummary.riskStatusPercent != null
    );

    if (!hasSummary) {
        return <NoSummaryMessage />;
    }

    return (
        <section className={styles.clinicSummary__bottomFullWidth} aria-label={t('CLINICAL_INFORMATION')}>
            <ClinicalBackground
                icon={personalIcon}
                title={t('CLINICAL_BACKGROUND')}
                description={summaryText || t('CLINICAL_SUMMARY_NOT_FOUND')}
                translatable={!!summaryText}
                isAiSummary
                userGuid={userGuid}
                showHistoryButton={true}
                latestSummary={latestSummary}
                historySummaries={historyData?.summaries}
            />
            {latestSummary?.riskStatusPercent != null && (
                <RiskStatusBar percentage={latestSummary.riskStatusPercent} />
            )}
            <ClinicalBackground
                icon={clinicIcon}
                title={t('CLINICAL_INSIGHTS')}
                description={clinicalInsightsText}
                translatable={!!clinicalInsightsText}
                userGuid={userGuid}
            />
            <ClinicRecommendations categories={undefined} />
        </section>
    );
};

function ClinicSummary({
    user,
    onClose,
}: {
    user: IUserReport;
    onClose: () => void;
}) {
    const { data, isLoading } = useGetClinicalUserSummaryQuery(user?.Users?.Guid ?? "", {
        skip: !user?.Users?.Guid,
    });

    const { data: historyData, isLoading: aiLoading } = useGetClinicalSummaryHistoryQuery(
        user?.Users?.Guid ?? "",
        {
            skip: !user?.Users?.Guid,
        }
    );
    const latestSummary = historyData?.clinicalSummary;

    const STATS_CONFIG = [
        // { 
        //     icon: intakeDayIcon, 
        //     title: t('DATE_RECEIPT'), 
        //     getValue: () => getPrintDate(new Date(user?.Users?.DateRegister), true), 
        //     colorClass: styles.clinicSummary__cardPink,
        //     needsLoading: false
        // },
        {
            icon: therapistReportsIcon,
            title: t('PATIENT_REPORTS'),
            getValue: (summaryData: TClinicSummaryResponce | undefined) => summaryData?.clientReportsNum,
            colorClass: styles.clinicSummary__cardTeal,
            needsLoading: true
        },
        {
            icon: activeProgramIcon,
            title: t('REPORTS_CAREGIVER'),
            getValue: (summaryData: TClinicSummaryResponce | undefined) => summaryData?.therapistReportsNum,
            colorClass: styles.clinicSummary__cardGreen2,
            needsLoading: true
        },
        {
            icon: randomMeetingsIcon,
            title: t('CASUAL_MEETINGS'),
            getValue: (summaryData: TClinicSummaryResponce | undefined) => summaryData?.randomMeetingsNum,
            colorClass: styles.clinicSummary__cardRed,
            needsLoading: true
        },
        {
            icon: MedicalReportsIcon,
            title: t('TREATMENT_MEETINGS'),
            getValue: (summaryData: TClinicSummaryResponce | undefined) => summaryData?.appointmentsProgramsMeetingsNum,
            colorClass: styles.clinicSummary__cardBlue2,
            textColRight: false,
            needsLoading: true
        },
        {
            icon: treatmentMeetingsIcon,
            title: t('ACTIVE_PLANS'),
            getValue: (summaryData: TClinicSummaryResponce | undefined) => summaryData?.activeAppointmentsProgramsNum,
            colorClass: styles.clinicSummary__cardGray,
            textColRight: false,
            needsLoading: true
        },
        {
            icon: inActiveProgramsIcon,
            title: t('PLANS_NOT_ACTIVE'),
            getValue: (summaryData: TClinicSummaryResponce | undefined) => summaryData?.nonActiveAppointmentsProgramsNum,
            colorClass: styles.clinicSummary__cardOrange,
            textColRight: false,
            needsLoading: true
        },
    ];

    return (
        <section className={styles.clinicSummary__profile} role="main">
            <BackArrowButton
                text={t('PREVIOUS_SCREEN')}
                onClick={onClose}
                position={"left"}
            />
            <h1 className={styles.clinicSummary__title}>{t('MENU_CLINIC_SUMMARY')}</h1>
            <section className={styles.clinicSummary__section} aria-label={t('PATIENT_INFORMATION')}>
                <div className={styles.clinicSummary__layout}>
                    <aside className={styles.clinicSummary__column} aria-label={t('PATIENT_DETAILS')}>
                        <ProfileCard icon={userCircleIcon}>
                            <h2 className={styles.clinicSummary__text}>
                                {user?.Users?.FirstName ?? ""} {user?.Users?.LastName}
                            </h2>
                            <div className={styles.clinicSummary__ageContainer}>
                                <img
                                    src={`${calendarIcon}`}
                                    alt={t('CALENDAR_IMAGE')}
                                    className={styles.clinicSummary__calendarIcon}
                                />
                                <span className={styles.clinicSummary__subText}>{t('AGE')}{user?.Users?.Birth ? calculateAge(user?.Users?.Birth) : t('NO_INFORMATION')}
                                </span>
                            </div>
                        </ProfileCard>

                        <ProfileCard
                            icon={locationCircleIcon}
                            iconColorClass={styles.clinicSummary__iconCircle_green}
                        >
                            <address className={styles.clinicSummary__text}>
                                {user?.Users?.Address ?? t('UNKNOWN')}
                            </address>
                        </ProfileCard>

                        <ProfileCard
                            icon={lockCircleIcon}
                            iconColorClass={styles.clinicSummary__iconCircle_green}
                        >
                            <p className={styles.clinicSummary__text}>
                                {user?.Users?.Account ?? t('UNKNOWN')}
                            </p>
                        </ProfileCard>
                    </aside>
                    <section className={styles.clinicSummary__grid} aria-label={t('CLINICAL_STATISTICS')}>
                        {STATS_CONFIG.map((stat, idx) => (
                            <ClinicSummaryStatCard
                                key={idx}
                                icon={stat.icon}
                                title={stat.title}
                                value={stat.getValue(data)}
                                colorClass={stat.colorClass}
                                isLoading={stat.needsLoading ? isLoading : false}
                                textColRight={stat.textColRight}
                            />
                        ))}

                    </section>
                </div>
            </section>
            {aiLoading ? (
                <Loader />
            ) : (
                <ClinicalContent
                    latestSummary={latestSummary}
                    historyData={historyData}
                    userGuid={user?.Users?.Guid ?? undefined}
                />
            )}
        </section>

    );
}

export default ClinicSummary;